<template>
  <div>
    <breadcrumb :items="breadcrumbs" />

    <v-container fluid>
      <div class="d-flex mb-6 align-center">
        <h2 class="font-weight-bold">{{ $t("carbonCredit.manageTrees.treeHistory") }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined class="action-btn-outline" @click="goBack">{{ $t("carbonCredit.manageTrees.back") }}</v-btn>
      </div>

      <v-card elevation="0" class="pa-5 history-card">
        <v-row align="center" class="mb-2">
          <v-col cols="12" md="3">
            <label class="filter-label">{{ $t("carbonCredit.manageTrees.updatedBy") }}</label>
            <v-select
              v-model="filters.updatedBy"
              :items="updatedByOptions"
              item-text="label"
              item-value="value"
              outlined
              dense
              clearable
              hide-details
              @change="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="3">
            <label class="filter-label">{{ $t("carbonCredit.manageTrees.startDate") }}</label>
            <v-text-field
              v-model="filters.startDate"
              type="date"
              outlined
              dense
              hide-details
              @change="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="3">
            <label class="filter-label">{{ $t("carbonCredit.manageTrees.endDate") }}</label>
            <v-text-field
              v-model="filters.endDate"
              type="date"
              outlined
              dense
              hide-details
              @change="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="3" class="d-flex justify-end align-center mt-6 mt-md-0">
            <div class="mr-3 caption grey--text">
              {{ pageFrom }}-{{ pageTo }} of {{ totalHistory }}
            </div>
            <v-btn
              fab
              x-small
              outlined
              color="primary"
              class="mr-2"
              :disabled="pagination.page <= 1"
              @click="changePage(-1)"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              fab
              x-small
              outlined
              color="primary"
              :disabled="pagination.page >= totalPages"
              @click="changePage(1)"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <template v-if="history.length">
          <v-card
            v-for="entry in history"
            :key="entry.id"
            elevation="0"
            class="history-entry mb-4"
          >
            <div class="entry-header px-4 py-2">{{ $t("carbonCredit.manageTrees.treeDetails") }}</div>
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" md="3" v-for="field in getFields(entry)" :key="field.label">
                  <div class="item-label">{{ field.label }}</div>
                  <div class="item-value">{{ field.value }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="item-label">{{ $t("carbonCredit.manageTrees.updatedBy") }}</div>
                  <div class="item-value">{{ entry.user?.fullName || "-" }}</div>
                  <div class="item-label mt-2">{{ $t("carbonCredit.manageTrees.updatedAt") }}</div>
                  <div class="item-value">{{ formatDateTime(entry.updated_at || entry.updatedAt) }}</div>
                </v-col>
              </v-row>

              <v-row class="mt-1">
                <v-col cols="12" md="8">
                  <div class="item-label">{{ $t("carbonCredit.manageTrees.whereWouldYouLikeToIncludeThisTree") }}</div>
                  <div class="item-value">{{ getProgramTypeText(entry) }}</div>
                  <div class="item-label mt-3">{{ $t("carbonCredit.manageTrees.additionalNotes") }}</div>
                  <div class="item-value">{{ entry.additional_note || "-" }}</div>
                  <div class="item-label mt-3">{{ $t("carbonCredit.manageTrees.treeImages") }}</div>
                  <div class="d-flex flex-wrap">
                    <div
                      v-for="(img, idx) in getImages(entry)"
                      :key="`${entry.id}-img-${idx}`"
                      class="image-wrap mr-2 mb-2"
                      @click="openImagePreview(img.url)"
                    >
                      <v-img :src="img.url" width="90" height="90" cover />
                    </div>
                    <div v-if="!getImages(entry).length" class="item-value">-</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </v-card>
    </v-container>

    <v-dialog v-model="imagePreviewDialog" max-width="760">
      <v-card>
        <v-card-title class="primary white--text">
          {{ $t("carbonCredit.manageTrees.treeImage") }}
          <v-spacer />
          <v-btn icon color="white" @click="closeImagePreview">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-5">
          <v-img :src="previewImageUrl" max-height="520" contain />
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer />
          <v-btn outlined color="primary" @click="closeImagePreview">{{ $t("carbonCredit.cancel") }}</v-btn>
          <v-btn color="primary" :disabled="!previewImageUrl" @click="downloadPreviewImage">
            <v-icon left>mdi-download</v-icon>
            {{ $t("carbonCredit.manageTrees.downloadImage") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import CarbonCreditService from "@/_services/CarbonCreditService";
import loadingMixin from "@/mixins/LoadingMixin";

export default {
  name: "TreeHistory",
  mixins: [loadingMixin],
  data() {
    return {
      fullPage: true,
      history: [],
      totalHistory: 0,
      filters: {
        updatedBy: null,
        startDate: null,
        endDate: null,
      },
      pagination: {
        page: 1,
        limit: 5,
      },
      imagePreviewDialog: false,
      previewImageUrl: "",
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", exact: true },
        { text: "Carbon Credit", disabled: false, href: "/carbon-credit/projects" },
        { text: "Manage Trees", disabled: false, href: "/carbon-credit/manage-trees" },
        { text: "Tree History", disabled: true },
      ],
    };
  },
  computed: {
    treeId() {
      return this.$route.params.id;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalHistory / this.pagination.limit));
    },
    pageFrom() {
      if (!this.totalHistory) return 0;
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },
    pageTo() {
      if (!this.totalHistory) return 0;
      return Math.min(this.totalHistory, this.pagination.page * this.pagination.limit);
    },
    updatedByOptions() {
      const seen = new Set();
      return this.history
        .map((entry) => ({
          value: entry.user?.id || null,
          label: entry.user?.fullName || "-",
        }))
        .filter((opt) => {
          if (!opt.value || seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });
    },
  },
  created() {
    this.fetchHistory();
  },
  methods: {
    async fetchHistory() {
      this.startLoading();
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          updated_by: this.filters.updatedBy || undefined,
          start_date: this.filters.startDate || undefined,
          end_date: this.filters.endDate || undefined,
        };
        const res = await CarbonCreditService.getTreeHistory(this.treeId, params);
        const payload = res?.data?.data || res?.data || {};
        this.history = Array.isArray(payload.history) ? payload.history : [];
        this.totalHistory = typeof payload.total === "number" ? payload.total : this.history.length;
      } catch (err) {
        this.$notify({
          text: err?.response?.data?.message || "Failed to load tree history",
          type: "error",
        });
        this.history = [];
        this.totalHistory = 0;
      } finally {
        this.stopLoading();
      }
    },
    applyFilters() {
      this.pagination.page = 1;
      this.fetchHistory();
    },
    changePage(step) {
      const next = this.pagination.page + step;
      if (next < 1 || next > this.totalPages) return;
      this.pagination.page = next;
      this.fetchHistory();
    },
    goBack() {
      this.$router.push({ name: "viewTree", params: { id: this.treeId } });
    },
    getFields(entry) {
      return [
        { label: this.$t("carbonCredit.manageTrees.dimitraAssetId"), value: entry.id || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeId"), value: entry.tree_id_by_user || "-" },
        { label: this.$t("carbonCredit.manageTrees.additionalId"), value: entry.additional_id || "-" },
        { label: this.$t("carbonCredit.user.farm"), value: entry.farm?.farmName || "-" },
        { label: this.$t("carbonCredit.manageTrees.zone"), value: entry.zone?.geofenceName || entry.zone_id || "-" },
        { label: this.$t("carbonCredit.manageTrees.gpsCoordinates"), value: this.getGpsText(entry) },
        { label: this.$t("carbonCredit.manageTrees.locationInformation"), value: entry.location_information || "-" },
        { label: this.$t("carbonCredit.manageTrees.altitude"), value: entry.altitude || "-" },
        { label: this.$t("carbonCredit.manageTrees.seedlingName"), value: entry.seedling_name || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeType"), value: entry.treeType?.name || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeSpecies"), value: entry.treeSpecies?.name || "-" },
        { label: this.$t("carbonCredit.manageTrees.dateOfPlantation"), value: this.formatDate(entry.date_planted) },
        { label: this.$t("carbonCredit.manageTrees.ageOfTree"), value: this.getAgeText(entry.date_planted) },
        { label: this.$t("carbonCredit.manageTrees.healthCondition"), value: entry.healthCondition?.name || "-" },
      ];
    },
    getGpsText(entry) {
      if (entry?.lat == null || entry?.lon == null) return "-";
      return `${entry.lat}, ${entry.lon}`;
    },
    getAgeText(date) {
      if (!date) return "-";
      const plantedAt = moment(date);
      if (!plantedAt.isValid()) return "-";
      const now = moment();
      if (plantedAt.isAfter(now)) return "-";

      const years = now.diff(plantedAt, "years");
      const afterYears = plantedAt.clone().add(years, "years");
      const months = now.diff(afterYears, "months");

      if (years <= 0 && months <= 0) return "0 Months";
      if (years > 0 && months > 0) {
        return `${years} Year${years === 1 ? "" : "s"} ${months} Month${months === 1 ? "" : "s"}`;
      }
      if (years > 0) return `${years} Year${years === 1 ? "" : "s"}`;
      return `${months} Month${months === 1 ? "" : "s"}`;
    },
    getProgramTypeText(entry) {
      if (!Array.isArray(entry.program_type) || !entry.program_type.length) return "-";
      return entry.program_type
        .map((item) => (typeof item === "object" ? item.name : item))
        .filter(Boolean)
        .join(", ") || "-";
    },
    getImages(entry) {
      const list = Array.isArray(entry.my_tree_images) ? entry.my_tree_images : [];
      return list
        .map((img) => ({
          url: img.file_url || img.fileUrl || img.url || img.location || "",
        }))
        .filter((img) => !!img.url);
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MMM-YYYY").toUpperCase();
    },
    formatDateTime(date) {
      if (!date) return "-";
      return moment(date).format("DD-MMM-YYYY | HH:mm:ss").toUpperCase();
    },
    openImagePreview(url) {
      if (!url) return;
      this.previewImageUrl = url;
      this.imagePreviewDialog = true;
    },
    closeImagePreview() {
      this.imagePreviewDialog = false;
      this.previewImageUrl = "";
    },
    downloadPreviewImage() {
      if (!this.previewImageUrl) return;
      const anchor = document.createElement("a");
      anchor.href = this.previewImageUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = `tree-history-image-${this.treeId}.jpg`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
  },
};
</script>

<style scoped lang="scss">
.history-card {
  border: 1px solid #efefef;
  border-radius: 8px;
}

.history-entry {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.entry-header {
  background: #f3f4f6;
  color: #1f8a54;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.filter-label,
.item-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 3px;
}

.item-value {
  font-size: 14px;
  color: #222;
  font-weight: 600;
  word-break: break-word;
}

.image-wrap {
  border: 1px solid #ececec;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.action-btn-outline {
  border-color: #00bd73 !important;
  color: #00bd73 !important;
}
</style>
